import { useEffect, useState } from "react";
import {
  investors as investorApi,
  portfolio as portfolioApi,
  quantfolio as qpApi,
  type Investor,
  type InvestorPortfolio as Portfolio,
} from "../../api";
import { Link, useRouter } from "../../router";
import { formatCurrency } from "./format";
import { OpenAccountWizard } from "./OpenAccountWizard";
import { SavingsPlans } from "./SavingsPlans";

const TYPE_LABELS: Record<string, string> = {
  ask: "ASK",
  ips: "IPS",
  ordinary: "Ordinary",
};

export function InvestorPortfolioView({ id }: { id: number }) {
  const { navigate } = useRouter();
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [wizard, setWizard] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [qpStatus, setQpStatus] = useState<{ ok: boolean; message: string } | null>(null);

  async function generateReport() {
    setReporting(true);
    try {
      await portfolioApi.generateReport(id);
      navigate(`/app/crm/${id}/documents`);
    } catch (e) {
      setError((e as Error).message);
      setReporting(false);
    }
  }

  // Push the client's Andersen accounts up to Quantfolio. Adopts a QP-side
  // pending account when present, updates a previously-synced one, else
  // creates — see server/services/qpAccountSync.ts.
  async function pushToQp() {
    setPushing(true);
    setQpStatus(null);
    try {
      const r = await qpApi.pushAccounts(id);
      const detail = [
        r.adopted ? `${r.adopted} adopted` : "",
        r.updated ? `${r.updated} updated` : "",
        r.created ? `${r.created} created` : "",
      ]
        .filter(Boolean)
        .join(", ");
      const n = r.accounts.length;
      setQpStatus({
        ok: true,
        message: `Pushed ${n} account${n === 1 ? "" : "s"} to Quantfolio${detail ? ` (${detail})` : ""}.`,
      });
    } catch (e) {
      setQpStatus({ ok: false, message: `Push to Quantfolio failed: ${(e as Error).message}` });
    } finally {
      setPushing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([investorApi.get(id), portfolioApi.forInvestor(id)])
      .then(([inv, p]) => {
        if (cancelled) return;
        setInvestor(inv.investor);
        setPortfolio(p.portfolio);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  if (error) {
    return (
      <div className="page">
        <Link to="/app/portfolio" className="back-link">
          ← All investors
        </Link>
        <div className="banner-error">{error}</div>
      </div>
    );
  }

  if (!investor || !portfolio) {
    return (
      <div className="page">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  const currencies = Object.keys(portfolio.by_currency);
  const primaryCurrency = currencies[0] ?? "NOK";

  return (
    <div className="page">
      <Link to="/app/portfolio" className="back-link">
        ← All investors
      </Link>

      <header className="page-head">
        <div>
          <span className="eyebrow">Portfolio</span>
          <h1>
            {investor.name}{" "}
            <span className={`pill stage-${investor.stage}`}>{investor.stage}</span>
            {investor.kyc_status !== "approved" && (
              <span className={`pill kyc-${investor.kyc_status}`}>
                KYC {investor.kyc_status}
              </span>
            )}
          </h1>
        </div>
        <div className="page-head-actions">
          {portfolio.accounts.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={generateReport}
              disabled={reporting}
            >
              {reporting ? "Generating…" : "Generate report"}
            </button>
          )}
          {portfolio.accounts.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={pushToQp}
              disabled={pushing}
              title="Publish these accounts and positions to Quantfolio"
            >
              {pushing ? "Pushing…" : "Push to Quantfolio"}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setWizard(true)}
          >
            Open account
          </button>
        </div>
      </header>

      {qpStatus && (
        <div className={qpStatus.ok ? "banner-success" : "banner-error"} role="status">
          {qpStatus.message}
        </div>
      )}

      {wizard && (
        <OpenAccountWizard
          investor={investor}
          onClose={() => setWizard(false)}
          onDone={(account) => {
            setWizard(false);
            navigate(`/app/portfolio/accounts/${account.id}`);
          }}
        />
      )}

      <section className="aggregate">
        {currencies.length === 1 ? (
          <div className="aggregate-figure">
            <span className="label">Total value</span>
            <span className="value">
              {formatCurrency(portfolio.total_value, primaryCurrency)}
            </span>
          </div>
        ) : (
          <div className="aggregate-multi">
            <div className="aggregate-figure">
              <span className="label">Total value (mixed currencies)</span>
              <span className="value muted">
                {currencies
                  .map((c) => formatCurrency(portfolio.by_currency[c]!, c))
                  .join(" + ")}
              </span>
            </div>
            <div className="aggregate-by-currency">
              {currencies.map((c) => (
                <div key={c} className="aggregate-pill">
                  <span className="label">{c}</span>
                  <span className="value">
                    {formatCurrency(portfolio.by_currency[c]!, c)}
                  </span>
                </div>
              ))}
              {portfolio.nok.total_nok != null && (
                <div className="aggregate-pill">
                  <span className="label">≈ NOK consolidated</span>
                  <span className="value">
                    {formatCurrency(portfolio.nok.total_nok, "NOK")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="aggregate-meta">
          <span>
            <strong>{portfolio.accounts.length}</strong> accounts
          </span>
          {portfolio.accounts.length > 0 && (
            <span>
              opened{" "}
              {portfolio.accounts.length === 1
                ? formatYear(portfolio.accounts[0]!.opened_at)
                : `${formatYear(earliest(portfolio.accounts.map((a) => a.opened_at)))}–${formatYear(latest(portfolio.accounts.map((a) => a.opened_at)))}`}
            </span>
          )}
        </div>
      </section>

      <h2 className="section-title">Accounts</h2>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Currency</th>
            <th>Opened</th>
            <th className="num">Value</th>
          </tr>
        </thead>
        <tbody>
          {portfolio.accounts.map((a) => (
            <tr
              key={a.id}
              className="row-link"
              onClick={() => navigate(`/app/portfolio/accounts/${a.id}`)}
            >
              <td className="strong">{a.name}</td>
              <td>{TYPE_LABELS[a.type] ?? a.type}</td>
              <td>{a.currency}</td>
              <td>{formatDate(a.opened_at)}</td>
              <td className="num">{formatCurrency(a.value, a.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SavingsPlans investorId={id} accounts={portfolio.accounts} />
    </div>
  );
}

function formatYear(s: string): string {
  return s.slice(0, 4);
}
function formatDate(s: string): string {
  return s.slice(0, 10);
}
function earliest(xs: string[]): string {
  return xs.length ? xs.reduce((a, b) => (a < b ? a : b)) : "";
}
function latest(xs: string[]): string {
  return xs.length ? xs.reduce((a, b) => (a > b ? a : b)) : "";
}
